/**
 * Print preview of the road CMR(s), like the Bill of Lading preview of the ocean pages: the
 * texts of every box are listed on the left and can be corrected, the PDF is shown on the
 * right. A multi-truck file has one CMR per truck, one tab each. Corrections change only the
 * print; nothing is saved on the file.
 */
import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Fade from "@mui/material/Fade";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { buildCmrTexts, cmrPdfUrl, downloadCmrPdf } from "constants/generateCmrPdf";

const MONO = { fontFamily: "Courier New, Courier, monospace", fontSize: "0.8rem" };

const fieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "10px" },
};

const HEADER_FIELDS = [
    { key: "cmrNumber", label: "CMR Number", half: true },
    { key: "plate", label: "18 · Plate Number", half: true },
    { key: "sender", label: "1 · Sender", rows: 3 },
    { key: "consignee", label: "2 · Consignee", rows: 3 },
    { key: "placeOfTakingOver", label: "3 · Place of taking over the goods", rows: 3 },
    { key: "placeOfDelivery", label: "4 · Place and date of delivery", rows: 3 },
    { key: "documentsAttached", label: "5 · Documents attached", rows: 2 },
    { key: "carrier", label: "16 · Carrier", rows: 2 },
    { key: "successiveCarrier", label: "17 · Successive carriers", rows: 2 },
    { key: "observations", label: "18 · Carrier reservations and observations", rows: 2 },
];

const FOOTER_FIELDS = [
    { key: "senderInstructions", label: "13 · Sender instructions", rows: 2 },
    { key: "specialAgreements", label: "19 · Special agreements", rows: 2 },
    { key: "paymentInstructions", label: "14 · Instructions as to payment", half: true },
    { key: "establishedPlace", label: "21 · Established in", half: true },
    { key: "establishedDate", label: "21 · Established on (DD/MM/YYYY)", half: true },
    { key: "senderSignature", label: "22 · Sender (signature box)", half: true },
];

const CARGO_COLUMNS = [
    { key: "packages", label: "7 · Packages", md: 2 },
    { key: "packing", label: "8 · Packing", md: 2 },
    { key: "goods", label: "9 · Nature of goods", md: 4, multiline: true },
    { key: "hsCode", label: "10 · HS code", md: 2 },
    { key: "gross", label: "11 · Gross kg", md: 1 },
    { key: "volume", label: "12 · m3", md: 1 },
];

const sectionTitle = (text) => (
    <MDTypography variant="button" fontWeight="bold" sx={{ display: "block", mt: 2, mb: 1, color: "#0f172a" }}>
        {text}
    </MDTypography>
);

const TextGrid = ({ fields, texts, set }) => (
    <Grid container spacing={1.5}>
        {fields.map(({ key, label, rows, half }) => (
            <Grid item xs={12} md={half ? 6 : 12} key={key}>
                <TextField
                    fullWidth
                    size="small"
                    label={label}
                    value={texts[key] || ""}
                    onChange={(e) => set(key, e.target.value)}
                    multiline={Boolean(rows)}
                    minRows={rows}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ style: MONO }}
                    sx={fieldSx}
                />
            </Grid>
        ))}
    </Grid>
);

function CmrTextsEditor({ texts, onChange }) {
    const set = (key, value) => onChange({ ...texts, [key]: value });
    const setCargo = (index, key, value) =>
        set(
            "cargo",
            texts.cargo.map((row, i) => (i === index ? { ...row, [key]: value } : row))
        );

    return (
        <MDBox>
            <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
                Corrections change only this print. Press &quot;Update preview&quot; to see them on the PDF.
            </MDTypography>
            {sectionTitle("Parties and places")}
            <TextGrid fields={HEADER_FIELDS} texts={texts} set={set} />

            {sectionTitle("Goods (6–12)")}
            {(texts.cargo || []).map((row, index) => (
                <MDBox key={`cargo-${index}`} sx={{ mb: 1, pb: 1, borderBottom: "1px dashed #e2e8f0" }}>
                    <Grid container spacing={1}>
                        {CARGO_COLUMNS.map(({ key, label, md, multiline }) => (
                            <Grid item xs={6} md={md} key={key}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={label}
                                    value={row[key] || ""}
                                    onChange={(e) => setCargo(index, key, e.target.value)}
                                    multiline={multiline}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ style: MONO }}
                                    sx={fieldSx}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </MDBox>
            ))}

            {sectionTitle("Instructions and signature")}
            <TextGrid fields={FOOTER_FIELDS} texts={texts} set={set} />
        </MDBox>
    );
}

const primaryButtonSx = {
    borderRadius: "12px",
    px: 3,
    py: 1.2,
    fontWeight: 700,
    color: "#fff !important",
    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
    boxShadow: "0 10px 25px rgba(25,118,210,0.28)",
    "&:hover": { background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)" },
};

/**
 * documents: [{ label, data }] where data is the shipment-shaped payload one CMR is printed
 * from (see cmrPayloadsForTrucks for road files, buildCmrPayloadForMbl for RMBLs).
 */
export default function CmrPrintDialog({ open, onClose, documents }) {
    const [textsList, setTextsList] = useState([]);
    const [active, setActive] = useState(0);
    const [previewUrl, setPreviewUrl] = useState("");
    const [dirty, setDirty] = useState(false);

    const showPreview = (texts) => {
        setPreviewUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return texts ? cmrPdfUrl(texts) : "";
        });
        setDirty(false);
    };

    useEffect(() => {
        if (!open) {
            showPreview(null);
            setTextsList([]);
            return;
        }
        const list = (documents || []).map((d) => buildCmrTexts(d.data));
        setTextsList(list);
        setActive(0);
        showPreview(list[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, documents]);

    const texts = textsList[active];

    const switchTo = (index) => {
        setActive(index);
        showPreview(textsList[index]);
    };

    const updateTexts = (next) => {
        setTextsList((list) => list.map((item, i) => (i === active ? next : item)));
        setDirty(true);
    };

    const downloadAll = () => textsList.forEach((item) => downloadCmrPdf(item));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{ sx: { borderRadius: "22px", overflow: "hidden" } }}
        >
            <MDBox
                sx={{
                    px: 3,
                    py: 2.5,
                    background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                    position: "relative",
                }}
            >
                <MDTypography variant="h5" fontWeight="bold" color="white">
                    CMR Preview
                </MDTypography>
                <MDTypography variant="button" color="white" sx={{ opacity: 0.8 }}>
                    Correct any text on the left, update the preview and download
                </MDTypography>
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 14,
                        top: 14,
                        color: "#fff",
                        backgroundColor: "rgba(255,255,255,0.10)",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </MDBox>

            {textsList.length > 1 && (
                <MDBox sx={{ px: 2, pt: 1.5, backgroundColor: "#f8fafc" }}>
                    <Tabs value={active} onChange={(_e, v) => switchTo(v)} variant="scrollable">
                        {(documents || []).map((d, i) => (
                            <Tab key={d.label || i} label={d.label || `CMR ${i + 1}`} sx={{ textTransform: "none" }} />
                        ))}
                    </Tabs>
                </MDBox>
            )}

            <DialogContent dividers sx={{ p: 2, backgroundColor: "#f8fafc", borderTop: "none", borderBottom: "none" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <MDBox
                            sx={{
                                height: 760,
                                overflowY: "auto",
                                borderRadius: "16px",
                                backgroundColor: "#f1f5f9",
                                border: "1px solid #e2e8f0",
                                p: 2,
                            }}
                        >
                            {texts && <CmrTextsEditor texts={texts} onChange={updateTexts} />}
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <MDBox
                            sx={{
                                borderRadius: "16px",
                                overflow: "hidden",
                                backgroundColor: "#fff",
                                boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
                                border: "1px solid #e2e8f0",
                            }}
                        >
                            {previewUrl ? (
                                <iframe
                                    src={previewUrl}
                                    title="CMR Preview"
                                    width="100%"
                                    height="760"
                                    style={{ border: "none", display: "block", background: "#fff" }}
                                />
                            ) : null}
                        </MDBox>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    backgroundColor: "#fff",
                    borderTop: "1px solid #eef2f7",
                    justifyContent: "space-between",
                }}
            >
                <Button onClick={onClose} variant="text" sx={{ color: "#64748b", fontWeight: 600 }}>
                    Close
                </Button>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                    {/* Shown only while the edited texts are not in the preview yet. */}
                    {dirty && (
                        <Button variant="contained" onClick={() => showPreview(texts)} sx={primaryButtonSx}>
                            Update preview
                        </Button>
                    )}
                    {textsList.length > 1 && (
                        <Button
                            variant="outlined"
                            onClick={downloadAll}
                            sx={{ borderRadius: "12px", px: 3, fontWeight: 700, color: "#1565c0 !important", borderColor: "#1565c0" }}
                        >
                            Download all ({textsList.length})
                        </Button>
                    )}
                    <Button variant="contained" onClick={() => texts && downloadCmrPdf(texts)} sx={primaryButtonSx}>
                        {textsList.length > 1 ? "Download this CMR" : "Download PDF"}
                    </Button>
                </MDBox>
            </DialogActions>
        </Dialog>
    );
}
